import { useState, type FunctionComponent } from "react";
import { PlayCircleOutlined, RollbackOutlined, UndoOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Empty, Popconfirm, Space, Table, Tag, Typography, message } from "antd";

import {
  ChallengeCompletionStatus,
  type ChallengeTaskCompletionEntity,
} from "@fitness-app/data-models/entities/Challenge";
import { type Trainee } from "@fitness-app/data-models/entities/Trainee";

import VideoPlayer from "~/components/VideoPlayer/VideoPlayer";
import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { supabase } from "~/store/initializeStore";
import { completionStatusColors, completionStatusLabels } from "./constants";
import { formatChallengeDate } from "./helpers";

type CompletionRow = ChallengeTaskCompletionEntity & {
  trainee: Pick<Trainee, "firstName" | "lastName" | "email"> | null;
  task: { title: string; points: number } | null;
};

type Props = {
  challengeId: string;
};

const COUNTED_STATUSES: ChallengeCompletionStatus[] = [
  ChallengeCompletionStatus.Auto,
  ChallengeCompletionStatus.Approved,
];

const ChallengeCompletionsTable: FunctionComponent<Props> = ({ challengeId }) => {
  const queryClient = useQueryClient();
  const { memberId } = useUserClaims();
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const queryKey = ["challenge-completions", challengeId];

  const completionsQuery = useQuery({
    queryKey,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("challenge_task_completion")
        .select("*, trainee:traineeId!inner(firstName, lastName, email), task:taskId(title, points)")
        .eq("challengeId", challengeId)
        .order("date", { ascending: false })
        .returns<CompletionRow[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  // Revoke a counted completion (→ rejected, 0 pts) or restore a rejected one (→ approved + points).
  const revokeMutation = useMutation({
    mutationFn: async ({ row, restore }: { row: CompletionRow; restore: boolean }) => {
      const { error } = await supabase
        .from("challenge_task_completion")
        .update({
          status: restore ? ChallengeCompletionStatus.Approved : ChallengeCompletionStatus.Rejected,
          pointsAwarded: restore ? row.task?.points ?? 0 : 0,
          verifiedAt: new Date().toISOString(),
          verifiedBy: memberId ?? null,
        } as never)
        .eq("id", row.id);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: async (_data, { restore }) => {
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: ["challenge-leaderboard", challengeId] });
      void message.success(restore ? "Zaliczenie przywrócone" : "Zaliczenie cofnięte");
    },
    onError: (error: Error) => {
      void message.error(`Operacja nie powiodła się: ${error.message}`);
    },
  });

  if (!completionsQuery.isFetching && (completionsQuery.data?.length ?? 0) === 0) {
    return <Empty description="Brak zaliczeń" />;
  }

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Typography.Text type="secondary">
        Cofnięcie lub przywrócenie zaliczenia zmienia punkty przy najbliższym przeliczeniu rankingu (co ~30 min).
      </Typography.Text>
      <Table<CompletionRow>
        dataSource={completionsQuery.data ?? []}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        loading={completionsQuery.isFetching}
        locale={{ emptyText: "Brak zaliczeń" }}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
      >
        <Table.Column<CompletionRow>
          title="Klientka"
          key="trainee"
          render={(_, row) => (
            <Space direction="vertical" size={0}>
              <Typography.Text strong>
                {row.trainee?.firstName} {row.trainee?.lastName}
              </Typography.Text>
              <Typography.Text type="secondary">{row.trainee?.email}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column<CompletionRow>
          title="Zadanie"
          key="task"
          render={(_, row) => row.task?.title ?? "-"}
        />
        <Table.Column<CompletionRow>
          title="Data"
          dataIndex="date"
          key="date"
          render={(date: string) => formatChallengeDate(date)}
        />
        <Table.Column<CompletionRow>
          title="Status"
          dataIndex="status"
          key="status"
          render={(status: string) => {
            const typed = status as ChallengeCompletionStatus;
            return <Tag color={completionStatusColors[typed] ?? "default"}>{completionStatusLabels[typed] ?? status}</Tag>;
          }}
        />
        <Table.Column<CompletionRow>
          title="Punkty"
          dataIndex="pointsAwarded"
          key="pointsAwarded"
          render={(points: number) => `${points} pkt`}
        />
        <Table.Column<CompletionRow>
          title="Dowód"
          dataIndex="proofUrl"
          key="proofUrl"
          render={(proofUrl: string | null) =>
            proofUrl ? (
              <Button size="small" icon={<PlayCircleOutlined />} onClick={() => setPlayingUrl(proofUrl)}>
                Obejrzyj
              </Button>
            ) : (
              <Typography.Text type="secondary">-</Typography.Text>
            )
          }
        />
        <Table.Column<CompletionRow>
          title="Akcje"
          key="actions"
          render={(_, row) => {
            const status = row.status as ChallengeCompletionStatus;
            const isLoadingRow = revokeMutation.isLoading && revokeMutation.variables?.row.id === row.id;

            if (COUNTED_STATUSES.includes(status)) {
              return (
                <Popconfirm
                  title="Cofnąć zaliczenie?"
                  description="Punkty zostaną odjęte przy najbliższym przeliczeniu."
                  okText="Cofnij"
                  okButtonProps={{ danger: true }}
                  cancelText="Anuluj"
                  onConfirm={() => revokeMutation.mutate({ row, restore: false })}
                >
                  <Button danger icon={<RollbackOutlined />} loading={isLoadingRow}>
                    Cofnij zaliczenie
                  </Button>
                </Popconfirm>
              );
            }

            if (status === ChallengeCompletionStatus.Rejected) {
              return (
                <Popconfirm
                  title="Przywrócić zaliczenie?"
                  okText="Przywróć"
                  cancelText="Anuluj"
                  onConfirm={() => revokeMutation.mutate({ row, restore: true })}
                >
                  <Button icon={<UndoOutlined />} loading={isLoadingRow}>
                    Przywróć
                  </Button>
                </Popconfirm>
              );
            }

            return <Typography.Text type="secondary">W kolejce akceptacji</Typography.Text>;
          }}
        />
      </Table>
      <VideoPlayer videoUrl={playingUrl} visible={Boolean(playingUrl)} onClose={() => setPlayingUrl(null)} />
    </Space>
  );
};

export default ChallengeCompletionsTable;
